import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import './SSOTermsAcceptance.scss';

interface SSOUserInfo {
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  provider: string;
}

const SSOTermsAcceptance: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const [userInfo, setUserInfo] = useState<SSOUserInfo | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    // Get user info from location state
    const state = location.state as { userInfo?: SSOUserInfo };
    if (state?.userInfo) {
      setUserInfo(state.userInfo);
    } else {
      // If no user info, redirect to register
      navigate('/register');
    }
  }, [location.state, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptTerms) {
      setError(t('errors.validation.acceptTerms'));
      return;
    }

    if (!userInfo) {
      setError('User information not available');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiService.registerWithTerms({
        user_info: userInfo,
        terms_accepted: acceptTerms
      });

      // Set auth token and user
      apiService.setAuthToken(response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Update the auth context with the new user
      updateUser(response.user);

      // Redirect based on user role
      if (response.user.role === 'barman') {
        navigate('/barman');
      } else if (response.user.role === 'customer') {
        navigate('/events');
      } else if (response.user.role === 'organizer') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Registration failed';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!userInfo) {
    return (
      <div className="sso-terms-page">
        <div className="container">
          <div className="sso-terms-page__content">
            <Card variant="elevated" padding="xl" className="sso-terms-card">
              <div className="sso-terms-card__loading">
                <p>{t('common.loading')}</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sso-terms-page">
      <div className="container">
        <div className="sso-terms-page__content">
          <Card variant="elevated" padding="xl" className="sso-terms-card">
            <div className="sso-terms-card__header">
              <h1 className="sso-terms-card__title">{t('auth.sso.termsTitle')}</h1>
              <p className="sso-terms-card__subtitle">
                {t('auth.sso.termsSubtitle', { 
                  name: userInfo.given_name || userInfo.name || userInfo.email,
                  provider: userInfo.provider 
                })}
              </p>
            </div>

            <div className="sso-terms-card__user-info">
              <div className="user-info">
                {userInfo.picture && (
                  <img 
                    src={userInfo.picture} 
                    alt="Profile" 
                    className="user-info__avatar"
                  />
                )}
                <div className="user-info__details">
                  <h3>{userInfo.given_name || userInfo.name || 'Usuario'}</h3>
                  <p>{userInfo.email}</p>
                  <span className="user-info__provider">
                    {t('auth.sso.connectedWith', { provider: userInfo.provider })}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="sso-terms-card__form">
              {error && (
                <div className="sso-terms-card__error">
                  {error}
                </div>
              )}

              <div className="sso-terms-card__terms">
                <label className="sso-terms-card__terms-label">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    disabled={isSubmitting}
                    className="sso-terms-card__terms-checkbox"
                  />
                  <span className="sso-terms-card__terms-text">
                    {t('auth.sso.acceptTerms')}{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="sso-terms-card__terms-link"
                    >
                      {t('auth.sso.viewTerms')}
                    </button>
                  </span>
                </label>
              </div>

              <div className="sso-terms-card__actions">
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  onClick={() => navigate('/register')}
                  disabled={isSubmitting}
                  className="sso-terms-card__back-btn"
                >
                  {t('common.back')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={!acceptTerms || isSubmitting}
                  className="sso-terms-card__submit-btn"
                >
                  {isSubmitting ? t('common.loading') : t('auth.sso.completeRegistration')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal__header">
              <h2>{t('auth.register.termsTitle')}</h2>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="terms-modal__close"
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>
            <div className="terms-modal__content">
              <div className="terms-modal__text">
                <h3>{t('auth.register.termsSection1.title')}</h3>
                <Trans i18nKey="auth.register.termsSection1.content"
                  components={{
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />
                <h3>{t('auth.register.termsSection2.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection2.content"
                  components={{
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />
                
                <h3>{t('auth.register.termsSection3.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection3.content"
                  components={{
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />
                
                <h3>{t('auth.register.termsSection4.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection4.content"
                  components={{
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection5.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection5.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection6.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection6.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection7.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection7.content"
                  components={{
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection8.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection8.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection9.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection9.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection10.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection10.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection11.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection11.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection12.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection12.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />
                
                <h3>{t('auth.register.termsSection13.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection13.content"
                  components={{
                    h6: <h6 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />
                
                <h3>{t('auth.register.termsSection14.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection14.content"
                  components={{
                    h6: <h6 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection15.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection15.content"
                  components={{
                    h5: <h5 />,
                    h6: <h6 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection16.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection16.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection17.title')}</h3>
                <Trans
                  i18nKey="auth.register.termsSection17.content"
                  components={{
                    h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />
              </div>
            </div>
            <div className="terms-modal__footer">
              <Button
                type="button"
                variant="primary"
                onClick={() => setShowTermsModal(false)}
              >
                {t('common.accept')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSOTermsAcceptance;
